import { MobileOfferAssignment, MobileOfferAssignmentStatus, ReassignMobileOfferRequest } from '@numeo/types'

const storageKey = (assignment: MobileOfferAssignment) => `mobile-offer-reassign:${assignment.userId}:${assignment.id}`

export function hasPendingReassignment(assignment: MobileOfferAssignment): boolean {
    try {
        return sessionStorage.getItem(storageKey(assignment)) !== null
    } catch {
        return false
    }
}

/** Retain the exact request only for this browser session until its outcome is reconciled. */
export function readPendingReassignment(assignment: MobileOfferAssignment): ReassignMobileOfferRequest | null {
    const stored = sessionStorage.getItem(storageKey(assignment))
    if (!stored) return null
    const value: unknown = JSON.parse(stored)
    if (
        !value ||
        typeof value !== 'object' ||
        !('expectedRevision' in value) ||
        !Number.isSafeInteger(value.expectedRevision) ||
        (value.expectedRevision as number) < 0 ||
        !('requestId' in value) ||
        typeof value.requestId !== 'string' ||
        !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(value.requestId) ||
        !('redeemBy' in value) ||
        typeof value.redeemBy !== 'string' ||
        !Number.isFinite(Date.parse(value.redeemBy)) ||
        !('reason' in value) ||
        typeof value.reason !== 'string' ||
        !value.reason.trim() ||
        value.reason.length > 500
    ) {
        throw new Error('Invalid pending reassignment')
    }
    return { expectedRevision: value.expectedRevision as number, requestId: value.requestId, redeemBy: value.redeemBy, reason: value.reason }
}

export function savePendingReassignment(assignment: MobileOfferAssignment, request: ReassignMobileOfferRequest): void {
    sessionStorage.setItem(storageKey(assignment), JSON.stringify(request))
}

export function clearPendingReassignment(assignment: MobileOfferAssignment): void {
    sessionStorage.removeItem(storageKey(assignment))
}

export function matchesReassignment(record: MobileOfferAssignment, original: MobileOfferAssignment, request: ReassignMobileOfferRequest): boolean {
    return (
        record.id === original.id &&
        record.userId === original.userId &&
        record.offerId === original.offerId &&
        record.provider === 'apple' &&
        (record.revision ?? 0) === request.expectedRevision + 1 &&
        record.redeemBy === request.redeemBy &&
        record.reason === request.reason
    )
}

export function canReassignOffer(record: MobileOfferAssignment): boolean {
    return (
        record.provider === 'apple' &&
        (record.status === MobileOfferAssignmentStatus.REVOKED ||
            record.status === MobileOfferAssignmentStatus.EXPIRED ||
            (record.status === MobileOfferAssignmentStatus.ASSIGNED && Date.parse(record.redeemBy) <= Date.now()))
    )
}
